import { timeout, TimeoutError } from "@package/util/promise";
import { logger } from "@artis/logger";

export type HealthCheckerOptions = {
  /** Timeout for tasks */
  taskTimeout: number;
  /** Function to rebuild the worker */
  rebuild: () => Promise<void>;
  /** Function to call after the worker is rebuilt */
  restart: () => Promise<void>;
  /** Function to check if the worker is healthy */
  health?: () => Promise<{ started: boolean }>;
};

export class HealthChecker {
  private static readonly CHECK_TARGET_INTERVAL = 5000;
  private static readonly BACKOFF_TIME_MS = 1000;
  private static readonly MAX_BACKOFF_MULTIPLIER = 32;

  constructor(private readonly options: HealthCheckerOptions) {}

  private healthTimer: ReturnType<typeof setTimeout> | null = null;
  private rebuildTimer: ReturnType<typeof setTimeout> | null = null;
  private rebuilding = false;
  private restarting = false;
  private failures = 0;
  private generation = 0;

  start = () => {
    this.stop();
    this.failures = 0;
    this.generation++;
    if (this.options.health) {
      let checking = false;
      this.healthTimer = setInterval(() => {
        if (checking) return;
        checking = true;
        this.task(() => Promise.resolve(this.options.health?.()))
          .then((r) => (r?.started === false ? this.options.restart() : undefined))
          .catch(() => {})
          .finally(() => {
            checking = false;
          });
      }, HealthChecker.CHECK_TARGET_INTERVAL);
    }
  };

  stop = () => {
    if (this.rebuildTimer) clearTimeout(this.rebuildTimer);
    this.rebuildTimer = null;
    if (this.healthTimer) clearInterval(this.healthTimer);
    this.healthTimer = null;
  };

  task = async <R>(fn: () => Promise<R>) => {
    if (this.rebuilding && !this.restarting) return Promise.reject(new Error("Worker is restarting"));
    const gen = this.generation;
    try {
      const result = await timeout(this.options.taskTimeout, fn());
      if (gen === this.generation) this.failures = 0;
      return result;
    } catch (e) {
      if (e instanceof TimeoutError && !this.restarting && gen === this.generation) {
        this.scheduleRestart();
      }
      throw e;
    }
  };

  private scheduleRestart = () => {
    if (this.rebuilding) return;
    this.rebuilding = true;
    this.failures++;
    this.stop();
    const backoff = Math.min(2 ** this.failures - 2, HealthChecker.MAX_BACKOFF_MULTIPLIER);
    this.rebuildTimer = setTimeout(() => this.restart(), HealthChecker.BACKOFF_TIME_MS * backoff);
  };

  private restart = async () => {
    try {
      logger.info("HealthChecker: rebuild");
      await this.options.rebuild();
      await new Promise((resolve) => setTimeout(resolve, HealthChecker.BACKOFF_TIME_MS));
    } catch (e) {
      this.failures++;
      logger.error("HealthChecker: rebuild failed", e);
      this.rebuilding = false;
      this.scheduleRestart();
      return;
    }
    this.restarting = true;
    try {
      logger.info("HealthChecker: restart");
      await this.options.restart();
    } catch (e) {
      this.failures++;
      logger.error("HealthChecker: restart failed", e);
      this.rebuilding = false;
      this.restarting = false;
      this.scheduleRestart();
      return;
    }
    this.restarting = false;
    this.rebuilding = false;
    this.start();
  };
}
